import React, { useState } from "react"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import { url } from "aldoo-ra/CMS/url"
import MenuGroup from "aldoo-ra/MenuGroup"
import PubSub from "pubsub-js"
import DynamicSVG from "aldoo-ra/DynamicSVG"
import parse from "html-react-parser"
import CMSRender from "aldoo-ra/CMS/cms-render"
import {PlusCircleIcon} from "@heroicons/react/24/outline"

// Component for the rotating plus button
const RotatingButton = ({ isOpen }) => {
  return (
    <div
      className={`w-6 h-6 aspect-1/1 flex items-center justify-center flex-shrink-0 ml-4 transition-transform ${
        isOpen ? "rotate-45" : ""
      }`}
    >
      <PlusCircleIcon className="w-6 h-6 text-text" />
    </div>
  )
}

// Helper function to build action map from menu items
const buildActionMap = (items) => {
  if (!items || !Array.isArray(items)) return {}
  return items.reduce((acc, item) => {
    if (!item.options || !Object.keys(item.options).length) return acc

    Object.keys(item.options).forEach((key) => {
      acc[item.options[key]] = () => {
        PubSub.publish(item.options[key])
      }
    })

    return acc
  }, {})
}

// Component for an accordion section
const AccordionSection = ({ section, isOpen, toggleSection }) => {
  const actionMap = buildActionMap(section?.items)

  return (
    <div className="border-b-2 border-bordered">
      <button
        className="w-full flex justify-between items-center py-4"
        onClick={() => toggleSection(section.name)}
      >
        <Typography variant="body1_accent">{section.label}</Typography>
        <RotatingButton isOpen={isOpen} />
      </button>

      {isOpen && (
        <MenuGroup
          key={`menu-${section.name}`}
          renderAs={"vlist"}
          name={section.name}
          styleOverrides={{
            menu: "pl-[16px] mt-[-20px]",
            menu_column: "border-none",
            menu_item: "px-0 my-4",
          }}
          actionMap={actionMap}
          rootItemRender={(menuItem) => (
            <Typography variant="body1_accent" className="hidden">
              {menuItem.label}
            </Typography>
          )}
          menuItemRender={(menuItem) => {
            return menuItem.link || menuItem.options ? (
              <Button variant="nav_menuitem">{menuItem.label}</Button>
            ) : (
              <Typography variant="txt1" className="hidden">
                {menuItem.label}
              </Typography>
            )
          }}
        />
      )}
    </div>
  )
}

// Component for footer logo and CTA section
const FooterLogoAndCTA = () => {
  return (
    <MenuGroup
      name="Footer Logo & CTA Group"
      renderAs="vlist"
      styleOverrides={{
        menu: "my-0",
        menu_column: "border-none my-0",
        menu_item: "my-0",
      }}
      menuItemRender={(item) => (
        <div>
          <img src={url(item.image)} className="w-[170px] mx-auto" />
          <Typography
            variant="h3"
            className="text-text text-center text-wrap mt-7"
          >
            {item.description && typeof item.description === 'string' ? parse(item.description) : item.description}
          </Typography>
        </div>
      )}
    />
  )
}

// Component for social media links
const SocialMediaLinks = () => {
  return (
    <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-4 mt-7">
      {/* Follow us - social links menu */}
      <MenuGroup
        name="social-oxford_dictionary"
        renderAs="hlist"
        variant="mobilingo_hlist_follow"
        menuItemRender={(item) => {
          return (
            <div className="flex items-center">
              {item.image && (
                <DynamicSVG 
                  url={item.image}
                  className="w-6 h-6 text-text hover:text-primary transition duration-200"
                />
              )}

              {!item.image && (
                <Typography 
                  variant="txt1"
                  className="!text-[15px] text-text whitespace-nowrap mr-2">
                  {item.label}
                </Typography>
              )}
            </div>
          )
        }}
      />
    
      <MenuGroup
        name="social-TT"
        renderAs="hlist"
        variant="mobilingo_hlist_follow"
        menuItemRender={(item) => {
          return (
            <div className="flex items-center">
              {item.image && (
                <DynamicSVG 
                  url={item.image}
                  className="w-6 h-6 text-text hover:text-primary transition duration-200"
                />
              )}

              {!item.image && (
                <Typography 
                  variant="txt1"
                  className="!text-[15px] text-text whitespace-nowrap mr-2">
                  {item.label}
                </Typography>
              )}
            </div>
          )
        }}
      />
    </div>
  )
}

// Main accordion sections component
const AccordionSections = ({ openSection, toggleSection }) => {
  return (
    <CMSRender
      contentType="MenuItem"
      match={{ "content.name": "FooterAccordionMenu" }}
      renderItem={(item) => (
        <div>
          {(item.items || []).map((section, index) => (
            <AccordionSection
              key={`${section.name}-${section._id || section.id || index}`}
              section={section}
              isOpen={openSection === section.name}
              toggleSection={toggleSection}
            />
          ))}
        </div>
      )}
    />
  )
}

const CopyrightGroup = () => {
  return (
    <MenuGroup 
      name="Footer Copyright Group" 
      renderAs="vlist"
      skipRootItem
      styleOverrides={{ 
        menu: "flex flex-col items-center gap-2 mt-7",
        menu_column: "hidden",
        menu_item: "flex items-center list-none px-0 py-0 my-0 rounded-md text-text text-center"
      }}
    
      menuItemRender={(item) => {
      if (item.link) {
        return;
      }
      return <Typography variant="txt1">{item.label}</Typography>;
      }}
    />
  )
}

// Main footer accordion component
const FooterAccordion = () => {
  const [openSection, setOpenSection] = useState(null)

  const toggleSection = (section) => {
    setOpenSection(openSection === section ? null : section)
  }

  return (
    <div className="w-full bg-paper px-[16px] md:px-px1_l pb-[40px] md:pb-[20px]">
      <div className="flex flex-col items-center mx-auto">
        <div className="w-full pt-[40px] pb-[40px] md:pb-[72px]">
          <AccordionSections
            openSection={openSection}
            toggleSection={toggleSection}
          />
        </div>

        <FooterLogoAndCTA />
        <SocialMediaLinks />
        <CopyrightGroup />
      </div>
    </div>
  )
}

export default FooterAccordion
